import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Tooltip,
  Badge,
  message,
  Popconfirm,
  Dropdown,
  Menu,
  Typography,
} from 'antd';
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  ReloadOutlined,
  MoreOutlined,
  FilterOutlined,
  ExportOutlined,
  DownOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import type { RangePickerProps } from 'antd/es/date-picker';

import { AppDispatch, RootState } from '../../../store/store';
import { STRAlert } from '../../../services/strService/strService';
import {
  getSTRAlertsThunk,
  hideSTRAlertThunk,
  showSTRAlertThunk,
  resolveSTRAlertThunk,
  bulkHideSTRAlertsThunk,
  hideAllAccountSTRAlertsThunk,
  hideRuleSTRAlertsForAccountThunk,
} from '../../../services/strService/strThunk';
import { setSelectedFilters, setPagination } from '../../../features/str/strSlice';

const { Text, Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const STRAlertManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { alerts, rules, ui } = useSelector((state: RootState) => state.str);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [showHideModal, setShowHideModal] = useState(false);
  const [hideReason, setHideReason] = useState('');
  const [currentAlert, setCurrentAlert] = useState<STRAlert | null>(null);
  const [bulkAction, setBulkAction] = useState<'hide' | 'resolve' | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    loadAlerts();
  }, [ui.selectedFilters, ui.pagination, ui.selectedDateRange]);

  const loadAlerts = () => {
    const params = {
      ...ui.selectedFilters,
      limit: ui.pagination.pageSize,
      offset: (ui.pagination.current - 1) * ui.pagination.pageSize,
      ...(ui.selectedDateRange && {
        dateFrom: ui.selectedDateRange[0],
        dateTo: ui.selectedDateRange[1],
      }),
    };
    dispatch(getSTRAlertsThunk(params));
  };

  const handleFilterChange = (key: string, value: any) => {
    dispatch(setSelectedFilters({ [key]: value }));
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    dispatch(setPagination({ current: page, pageSize }));
  };

  const handleHideAlert = async (alert: STRAlert) => {
    setCurrentAlert(alert);
    setShowHideModal(true);
  };

  const handleShowAlert = async (alert: STRAlert) => {
    try {
      await dispatch(showSTRAlertThunk(alert.id));
      loadAlerts();
    } catch (error) {
      message.error('Failed to show alert');
    }
  };

  const handleResolveAlert = async (alert: STRAlert) => {
    try {
      await dispatch(resolveSTRAlertThunk(alert.id));
      loadAlerts();
    } catch (error) {
      message.error('Failed to resolve alert');
    }
  };

  const handleBulkHide = async () => {
    setBulkAction('hide');
    setShowHideModal(true);
  };

  const handleBulkResolve = async () => {
    Modal.confirm({
      title: 'Confirm Bulk Resolve',
      content: `Are you sure you want to resolve ${selectedRowKeys.length} selected alerts?`,
      onOk: async () => {
        try {
          for (const alertId of selectedRowKeys) {
            await dispatch(resolveSTRAlertThunk(alertId as string));
          }
          setSelectedRowKeys([]);
          loadAlerts();
        } catch (error) {
          message.error('Failed to resolve alerts');
        }
      },
    });
  };

  const handleHideAllAccountAlerts = async (agreementId: string) => {
    Modal.confirm({
      title: 'Hide All Account Alerts',
      content: (
        <div>
          <p>Are you sure you want to hide all alerts for this account?</p>
          <Input.TextArea
            placeholder="Please provide a reason for hiding all alerts"
            onChange={(e) => setHideReason(e.target.value)}
            rows={3}
          />
        </div>
      ),
      onOk: async () => {
        if (!hideReason.trim()) {
          message.error('Please provide a reason');
          return;
        }
        try {
          await dispatch(hideAllAccountSTRAlertsThunk({ agreementId, reason: hideReason }));
          setHideReason('');
          loadAlerts();
        } catch (error) {
          message.error('Failed to hide account alerts');
        }
      },
    });
  };

  const handleHideRuleAlerts = async (agreementId: string, ruleCode: string) => {
    Modal.confirm({
      title: `Hide All ${ruleCode} Alerts`,
      content: (
        <div>
          <p>Are you sure you want to hide all {ruleCode} alerts for this account?</p>
          <Input.TextArea
            placeholder="Please provide a reason for hiding rule alerts"
            onChange={(e) => setHideReason(e.target.value)}
            rows={3}
          />
        </div>
      ),
      onOk: async () => {
        if (!hideReason.trim()) {
          message.error('Please provide a reason');
          return;
        }
        try {
          await dispatch(
            hideRuleSTRAlertsForAccountThunk({ agreementId, ruleCode, reason: hideReason })
          );
          setHideReason('');
          loadAlerts();
        } catch (error) {
          message.error('Failed to hide rule alerts');
        }
      },
    });
  };

  const submitHideAction = async () => {
    if (!hideReason.trim()) {
      message.error('Please provide a reason');
      return;
    }

    try {
      if (bulkAction === 'hide') {
        await dispatch(
          bulkHideSTRAlertsThunk({
            alertIds: selectedRowKeys as string[],
            reason: hideReason,
          })
        );
        setSelectedRowKeys([]);
      } else if (currentAlert) {
        await dispatch(hideSTRAlertThunk({ alertId: currentAlert.id, reason: hideReason }));
      }

      setShowHideModal(false);
      setHideReason('');
      setCurrentAlert(null);
      setBulkAction(null);
      loadAlerts();
    } catch (error) {
      message.error('Failed to hide alert(s)');
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'HIGH':
        return 'red';
      case 'MEDIUM':
        return 'orange';
      case 'LOW':
        return 'green';
      default:
        return 'blue';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'HIGH':
        return <ExclamationCircleOutlined />;
      case 'MEDIUM':
        return <WarningOutlined />;
      case 'LOW':
        return <InfoCircleOutlined />;
      default:
        return <InfoCircleOutlined />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'red';
      case 'RESOLVED':
        return 'green';
      case 'HIDDEN':
        return 'orange';
      case 'DISMISSED':
        return 'gray';
      default:
        return 'blue';
    }
  };

  const getActionMenu = (alert: STRAlert) => (
    <Menu>
      {!alert.isHidden && (
        <Menu.Item key="hide" onClick={() => handleHideAlert(alert)}>
          <EyeInvisibleOutlined /> Hide Alert
        </Menu.Item>
      )}
      {alert.isHidden && (
        <Menu.Item key="show" onClick={() => handleShowAlert(alert)}>
          <EyeOutlined /> Show Alert
        </Menu.Item>
      )}
      {alert.status === 'ACTIVE' && (
        <Menu.Item key="resolve" onClick={() => handleResolveAlert(alert)}>
          <CheckCircleOutlined /> Resolve Alert
        </Menu.Item>
      )}
      <Menu.Divider />
      <Menu.Item key="hide-all" onClick={() => handleHideAllAccountAlerts(alert.agreementId)}>
        <EyeInvisibleOutlined /> Hide All Account Alerts
      </Menu.Item>
      <Menu.Item
        key="hide-rule"
        onClick={() => handleHideRuleAlerts(alert.agreementId, alert.ruleCode)}
      >
        <EyeInvisibleOutlined /> Hide All {alert.ruleCode} Alerts
      </Menu.Item>
    </Menu>
  );

  const columns: ColumnsType<STRAlert> = [
    {
      title: 'Alert ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      render: (id: string) => (
        <Text code style={{ fontSize: 11 }}>
          {id.substring(0, 8)}...
        </Text>
      ),
    },
    {
      title: 'Agreement No',
      dataIndex: ['accountDetails', 'agreementNo'],
      key: 'agreementNo',
      width: 130,
      render: (agreementNo: string) => (
        <Text strong style={{ fontSize: 12 }}>
          {agreementNo}
        </Text>
      ),
    },
    {
      title: 'Rule',
      dataIndex: 'ruleCode',
      key: 'ruleCode',
      width: 80,
      render: (ruleCode: string) => <Tag color="blue">{ruleCode}</Tag>,
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      render: (severity: string) => (
        <Tag color={getSeverityColor(severity)} icon={getSeverityIcon(severity)}>
          {severity}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string, record: STRAlert) => (
        <div>
          <Tag color={getStatusColor(status)}>{status}</Tag>
          {record.isHidden && (
            <Tag color="orange">
              <EyeInvisibleOutlined /> Hidden
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Alert Message',
      dataIndex: 'alertMessage',
      key: 'alertMessage',
      width: 300,
      render: (message: string) => (
        <Tooltip title={message}>
          <Text style={{ fontSize: 12 }}>
            {message.length > 50 ? `${message.substring(0, 50)}...` : message}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (createdAt: string) => (
        <Text style={{ fontSize: 11 }}>{moment(createdAt).format('MM/DD HH:mm')}</Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record: STRAlert) => (
        <Space size="small">
          <Dropdown overlay={getActionMenu(record)} trigger={['click']}>
            <Button size="small" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record: STRAlert) => ({
      disabled: record.status === 'RESOLVED',
    }),
  };

  const renderFilters = () => (
    <Card size="small" className="mb-3" bodyStyle={{ padding: '12px' }}>
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} md={6}>
          <Select
            placeholder="Select Severity"
            value={ui.selectedFilters.severity}
            onChange={(value) => handleFilterChange('severity', value)}
            allowClear
            style={{ width: '100%' }}
          >
            <Option value="HIGH">High</Option>
            <Option value="MEDIUM">Medium</Option>
            <Option value="LOW">Low</Option>
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Select
            placeholder="Select Status"
            value={ui.selectedFilters.status}
            onChange={(value) => handleFilterChange('status', value)}
            allowClear
            style={{ width: '100%' }}
          >
            <Option value="ACTIVE">Active</Option>
            <Option value="RESOLVED">Resolved</Option>
            <Option value="HIDDEN">Hidden</Option>
            <Option value="DISMISSED">Dismissed</Option>
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Select
            placeholder="Select Rule"
            value={ui.selectedFilters.ruleCode}
            onChange={(value) => handleFilterChange('ruleCode', value)}
            allowClear
            style={{ width: '100%' }}
          >
            {rules.data.map((rule) => (
              <Option key={rule.ruleCode} value={rule.ruleCode}>
                {rule.ruleCode} - {rule.ruleName}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Input
            placeholder="Search Agreement ID"
            value={ui.selectedFilters.agreementId}
            onChange={(e) => handleFilterChange('agreementId', e.target.value)}
            allowClear
            prefix={<SearchOutlined />}
          />
        </Col>
      </Row>
    </Card>
  );

  const renderBulkActions = () => (
    <Space size="small" className="mb-3">
      <Button
        type="primary"
        danger
        onClick={handleBulkHide}
        disabled={selectedRowKeys.length === 0}
        icon={<EyeInvisibleOutlined />}
        size="small"
      >
        Hide ({selectedRowKeys.length})
      </Button>
      <Button
        type="primary"
        onClick={handleBulkResolve}
        disabled={selectedRowKeys.length === 0}
        icon={<CheckCircleOutlined />}
        size="small"
      >
        Resolve ({selectedRowKeys.length})
      </Button>
    </Space>
  );

  return (
    <div className="space-y-3">
      <Card bodyStyle={{ padding: '16px' }}>
        <div className="flex justify-between items-center mb-3">
          <Title level={5} style={{ margin: 0 }}>
            STR Alert Management
          </Title>
          <Space size="small">
            <Button
              icon={<FilterOutlined />}
              onClick={() => setShowFilters(!showFilters)}
              type={showFilters ? 'primary' : 'default'}
              size="small"
            >
              Filters
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadAlerts}
              loading={alerts.isLoading}
              size="small"
            >
              Refresh
            </Button>
          </Space>
        </div>

        {showFilters && renderFilters()}
        {selectedRowKeys.length > 0 && renderBulkActions()}

        <Table
          columns={columns}
          dataSource={alerts.data}
          rowKey="id"
          rowSelection={rowSelection}
          loading={alerts.isLoading}
          pagination={{
            current: ui.pagination.current,
            pageSize: ui.pagination.pageSize,
            total: alerts.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} alerts`,
            onChange: handlePaginationChange,
            onShowSizeChange: handlePaginationChange,
          }}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>

      {/* Hide Alert Modal */}
      <Modal
        title={bulkAction === 'hide' ? 'Hide Selected Alerts' : 'Hide Alert'}
        open={showHideModal}
        onOk={submitHideAction}
        onCancel={() => {
          setShowHideModal(false);
          setHideReason('');
          setCurrentAlert(null);
          setBulkAction(null);
        }}
        okText="Hide"
        cancelText="Cancel"
      >
        <div className="space-y-4">
          <Text>
            {bulkAction === 'hide'
              ? `Are you sure you want to hide ${selectedRowKeys.length} selected alerts?`
              : `Are you sure you want to hide this alert?`}
          </Text>
          <div>
            <Text strong>Reason for hiding:</Text>
            <TextArea
              value={hideReason}
              onChange={(e) => setHideReason(e.target.value)}
              placeholder="Please provide a reason for hiding the alert(s)"
              rows={3}
              required
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default STRAlertManagement;
